import { NavLink, useNavigate, useSearchParams } from 'react-router-dom';
import { useMemo } from 'react';
import Cookies from 'js-cookie';
import useTheme from 'chat-ui/src/hooks/useTheme';
import { CookieKey, UrlQueryKey } from '@common/ts/enums';
import { ECompanyThemeName } from '@/enums';
import Image from '@/components/atoms/Image';
import NotFoundImg from '@/assets/illustrations/unauth2-illustration.svg';
import TelletLogo from '@/assets/logo/tellet-logo-main.svg';
import EmpathyLogo from '@/assets/logo/empathy-logo-black.svg';
import { Button, buttonVariants } from '@/components/ui/button';
import Routes from '@/router/routes';
import { useLogout } from '@/hooks/useUser';
import useUnauthorizedContent from '@/hooks/useUnauthorizedContent';

// Move theme logos outside component to prevent recreation on each render
const themeLogosList = {
  [ECompanyThemeName.DEFAULT]: (
    <Image src={TelletLogo} alt="logo" className="absolute w-28 h-fit top-5 left-5" />
  ),
  [ECompanyThemeName.EMPATHY]: (
    <Image src={EmpathyLogo} alt="logo" className="absolute w-28 h-fit top-5 left-5" />
  ),
};

function Unauthorized() {
  const navigate = useNavigate();
  const { themeName } = useTheme();
  const [searchParams] = useSearchParams();
  const logout = useLogout();

  const mainOrganization = useMemo(() => Cookies.get(CookieKey.MAIN_ORGANIZATION), []);
  const fallbackWorkspace = useMemo(() => Cookies.get(CookieKey.FALLBACK_WORKSPACE), []);

  const message = searchParams.get(UrlQueryKey.MESSAGE) || '';

  const { title, description } = useUnauthorizedContent(message);

  const handleRedirect = () => {
    // Validate both organization and workspace exist before navigation
    if (fallbackWorkspace && mainOrganization) {
      navigate(
        `${Routes.organization.path}/${mainOrganization}${Routes.workspace.path}/${fallbackWorkspace}`,
      );
    } else {
      logout();
    }
  };

  return (
    <div className="relative flex items-center justify-center min-h-screen px-4 mx-auto text-center">
      <NavLink to="/">{themeLogosList[themeName]}</NavLink>

      <div className="flex flex-col items-center justify-center gap-2 mx-auto text-center">
        <Image
          effect="blur"
          src={NotFoundImg}
          alt="illustration"
          className="min500:w-full w-[450px]"
        />
        <h1 className="text-5xl font-bold mt-14 text-dark min500:text-4xl">{title}</h1>
        <p className="mt-2 text-lg font-medium text-light-dark max-w-[600px]">{description}</p>

        <Button
          onClick={handleRedirect}
          className={buttonVariants({
            variant: 'default',
            className: 'mt-5 transition-all ease-in-out hover:!bg-darker-primary',
          })}
        >
          Back to home
        </Button>
      </div>
    </div>
  );
}

export default Unauthorized;
